import { useRef, useState, useCallback, useEffect } from 'react';
import type { Swiper as SwiperType } from 'swiper';

export interface UseSwiperNavigationOptions {
  /**
   * Optional condition to skip edge state updates (e.g., when isMobile is true).
   * If provided and returns true, edge state will not be updated.
   */
  shouldSkipUpdate?: (swiper: SwiperType) => boolean;
  /**
   * Initial disabled state. Defaults to false.
   */
  initialDisabled?: boolean;
  /**
   * Dependencies array that triggers Swiper update when changed.
   * If not provided, Swiper will not be automatically updated.
   */
  updateDependencies?: React.DependencyList;
  /**
   * Optional condition to check if Swiper should be updated.
   * If provided and returns false, the update will be skipped.
   */
  shouldUpdate?: boolean;
}

export interface UseSwiperNavigationReturn {
  /**
   * Whether the swiper is disabled (both at beginning and end).
   */
  isSwiperDisabled: boolean;
  /**
   * Handler function to call on resize, afterInit, and slideChange events.
   */
  handleOnResize: (swiper: SwiperType) => void;
  /**
   * Ref for the previous navigation button.
   */
  prevRef: React.MutableRefObject<HTMLElement | null>;
  /**
   * Ref for the next navigation button.
   */
  nextRef: React.MutableRefObject<HTMLElement | null>;
  /**
   * Ref for the Swiper instance.
   */
  swiperRef: React.MutableRefObject<SwiperType | null>;
}

/**
 * Combined hook to manage Swiper edge state, navigation refs, and automatic updates.
 *
 * Provides state management for detecting when a Swiper instance is at both
 * the beginning and end (indicating no scrolling is possible), along with
 * refs for navigation buttons and automatic Swiper updates when dependencies change.
 *
 * @param options - Configuration options for the hook.
 * @returns Object containing edge state, handler function, navigation refs, and swiper ref.
 *
 * @example
 * ```tsx
 * const { isSwiperDisabled, handleOnResize, prevRef, nextRef, swiperRef } = useSwiperNavigation({
 *   initialDisabled: isMobile,
 *   shouldSkipUpdate: () => Boolean(isMobile),
 *   updateDependencies: [items],
 * });
 *
 * <Swiper
 *   onSwiper={instance => { swiperRef.current = instance; }}
 *   onAfterInit={handleOnResize}
 *   onResize={handleOnResize}
 *   onSlideChange={handleOnResize}
 *   navigation={{
 *     prevEl: prevRef.current,
 *     nextEl: nextRef.current,
 *   }}
 * >
 *   ...
 * </Swiper>
 * ```
 */
export function useSwiperNavigation(
  options: UseSwiperNavigationOptions = {},
): UseSwiperNavigationReturn {
  const {
    shouldSkipUpdate,
    initialDisabled = false,
    updateDependencies,
    shouldUpdate = true,
  } = options;

  const [isSwiperDisabled, setIsSwiperDisabled] = useState<boolean>(initialDisabled);
  const prevRef = useRef<HTMLElement>(null);
  const nextRef = useRef<HTMLElement>(null);
  const swiperRef = useRef<SwiperType | null>(null);

  const handleOnResize = useCallback(
    (swiper: SwiperType) => {
      if (shouldSkipUpdate?.(swiper)) {
        return;
      }

      if (swiper.isBeginning && swiper.isEnd) {
        setIsSwiperDisabled(true);
      } else {
        setIsSwiperDisabled(false);
      }
    },
    [shouldSkipUpdate],
  );

  // Handle automatic Swiper updates when dependencies change
  useEffect(() => {
    if (!updateDependencies || !shouldUpdate || !swiperRef.current) {
      return;
    }

    swiperRef.current.update();

    // Update edge state after DOM changes
    setTimeout(() => {
      if (swiperRef.current) {
        handleOnResize(swiperRef.current);
      }
    }, 0);
  }, updateDependencies);

  return {
    isSwiperDisabled,
    handleOnResize,
    prevRef,
    nextRef,
    swiperRef,
  };
}
